import { parseLigneCommandeDetails } from '../../utils/commandeLigneCommentaire';
import { formatOrderOptionBusinessLines } from '../../utils/orderOptionSnapshot';

function IngredientQtyList({ title, ingredients }) {
  if (!ingredients?.length) return null;

  const totalQty = ingredients.reduce((sum, ing) => sum + (ing.qty || 1), 0);

  return (
    <div className="mt-2">
      <div className="flex items-baseline justify-between gap-2 mb-1.5">
        {title ? (
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{title}</p>
        ) : (
          <span />
        )}
        <span className="text-xs text-gray-500 tabular-nums">
          {totalQty} portion{totalQty > 1 ? 's' : ''}
        </span>
      </div>
      <ul className="divide-y divide-gray-200/80 rounded-md border border-gray-200/80 bg-white overflow-hidden">
        {ingredients.map((ing, i) => (
          <li
            key={`${ing.nom}-${i}`}
            className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-4 px-3 py-2 text-sm"
          >
            <span className="text-gray-800 leading-snug text-left">{ing.nom}</span>
            <span className="justify-self-end flex-shrink-0 min-w-[2.5rem] text-right font-bold tabular-nums text-primary-700 bg-primary-50 rounded-md px-2 py-0.5">
              ×{ing.qty || 1}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function DetailList({ title, items, bullet = '•' }) {
  if (!items?.length) return null;
  return (
    <div className="mt-2">
      {title ? (
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">{title}</p>
      ) : null}
      <ul className="space-y-1">
        {items.map((item, i) => (
          <li key={`${item}-${i}`} className="flex gap-2 text-sm text-gray-700 leading-snug">
            <span className="text-primary-600 font-bold flex-shrink-0">{bullet}</span>
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Détail lisible d'une ligne commande (planche, composition, options).
 * Options = format métier partagé (cardinalité snapshot / legacy qty).
 */
export default function CommandeLigneDetails({ commentaire, options, className = '' }) {
  const parsed = parseLigneCommandeDetails(commentaire, options);
  const optionLabels = formatOrderOptionBusinessLines(options);

  if (!parsed && optionLabels.length === 0) return null;

  return (
    <div
      className={`rounded-lg bg-gray-50 border border-gray-100 px-3 py-2.5 space-y-2 ${className}`.trim()}
    >
      {parsed?.kind === 'planche_perso' && (
        <>
          <p className="text-sm font-semibold text-gray-800">
            Planche personnalisée
            {parsed.nbPersons ? (
              <span className="font-normal text-gray-600"> · {parsed.nbPersons} pers.</span>
            ) : null}
          </p>
          <IngredientQtyList title="Ingrédients" ingredients={parsed.ingredients} />
        </>
      )}

      {parsed?.kind === 'planche_variee' && (
        <p className="text-sm font-semibold text-gray-800">
          Planche variée
          {parsed.nbPersons ? (
            <span className="font-normal text-gray-600"> · {parsed.nbPersons} pers.</span>
          ) : null}
          <span className="block text-xs font-normal text-gray-500 mt-0.5">
            Composition laissée au commerçant
          </span>
        </p>
      )}

      {parsed?.kind === 'composition' && (
        <>
          <p className="text-sm font-semibold text-gray-800">{parsed.title}</p>
          <DetailList title="Composition" items={parsed.items} />
        </>
      )}

      {parsed?.kind === 'multiline' && (
        <ul className="space-y-1">
          {parsed.lines.map((line, i) => (
            <li key={i} className="text-sm text-gray-700 leading-snug whitespace-pre-line">
              {line}
            </li>
          ))}
        </ul>
      )}

      {parsed?.kind === 'plain' && (
        <p className="text-sm text-gray-700 whitespace-pre-line leading-relaxed">{parsed.text}</p>
      )}

      {optionLabels.length > 0 && (
        <div className={parsed ? 'pt-2 border-t border-gray-200' : ''}>
          <DetailList title="Options" items={optionLabels} />
        </div>
      )}
    </div>
  );
}
